import Vue from 'vue';
import {
  dataUrlRegex,
  ErrorMessages,
  globalPlainUrlRegex,
  globalUrlRegex,
  iframeRegex,
  LobbyTypes,
  phoneNumber,
  plainUrlRegex,
  urlAnchorRegex,
  urlRegex,
  zipCode,
} from '../constants';

Vue.mixin({
  methods: {
    capitalizeWords(phrase) {
      if (phrase) {
        const words = phrase.split(' ');
        const capitalizedWords = words.map(word => {
          return word.charAt(0).toUpperCase() + word.slice(1);
        });
        return capitalizedWords.join(' ');
      } else {
        return '';
      }
    },
    capitalizeFirstLetter(text) {
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
    getFirstLetter(text) {
      return text.charAt(0).toUpperCase();
    },
    scrollToTop() {
      return window.scroll({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });
    },
    getEmbedUrl(url) {
      const youtubeRegex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
      const vimeoRegex = /(?:vimeo\.com\/(?:.*#|.*\/videos\/)?([0-9]+))/;
      const facebookRegex = /(?:https?:\/\/)?(?:www\.)?facebook\.com\/(?:watch\/?\??v=([a-zA-Z0-9_-]+)|reel\/(\d+))/i;
      // const twitterRegex = /(?:x\.com\/(?:.*\/status\/))([0-9]+)/; TO DO with X API
      const tiktokRegex = /(?:tiktok\.com\/(?:.*\/video\/))([0-9]+)/;
      const instagramRegex = /(?:https?:\/\/(?:www\.)?instagram\.com\/(?:p|reel|reels|tv)\/([\w-]+))/;

      if (youtubeRegex.test(url)) {
        const videoId = url.match(youtubeRegex)[1];
        return `https://www.youtube.com/embed/${videoId}`;
      } else if (vimeoRegex.test(url)) {
        const videoId = url.match(vimeoRegex)[1];
        return `https://player.vimeo.com/video/${videoId}`;
      } else if (facebookRegex.test(url)) {
        const match = url.match(facebookRegex);
        const videoId = match[1];
        const reelId = match[2];

        if (reelId) {
          return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(`https://www.facebook.com/reel/${reelId}`)}&show_text=0`;
        } else if (videoId) {
          return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(`https://www.facebook.com/watch/?v=${videoId}`)}&show_text=0`;
        }
      } else if (tiktokRegex.test(url)) {
        const videoId = url.match(tiktokRegex)[1];
        return `https://www.tiktok.com/embed/v2/${videoId}`;
      } else if (instagramRegex.test(url)) {
        const videoId = url.match(instagramRegex)[1];
        return `https://www.instagram.com/p/${videoId}/embed`;
      } else {
        console.warn('Unsupported video URL format.');
        return null;
      }
    },
    socialMediaName(url) {
      if (url.includes('youtube')) {
        return 'YouTube';
      } else if (url.includes('vimeo')) {
        return 'Vimeo';
      } else if (url.includes('facebook')) {
        return 'Facebook';
      } else if (url.includes('twitter')) {
        return 'Twitter';
      } else if (url.includes('instagram')) {
        return 'Instagram';
      } else if (url.includes('tiktok')) {
        return 'TikTok';
      } else {
        return 'Website';
      }
    },
    cleanUrl(url) {
      return url.replace(/<\/?[a-z][\s\S]*?>/gi, '').replace(/["'>]+$/, '');
    },
    textIncludeUrl(content) {
      const match = content.match(urlRegex);

      let url = null;
      if (match) {
        url = this.cleanUrl(match[1]);
      } else {
        const plainMatch = content.match(plainUrlRegex);
        if (plainMatch) {
          url = this.cleanUrl(plainMatch[0]);
        }
      }

      return url;
    },
    getAllUrls(content) {
      let matches = [...content.matchAll(globalUrlRegex)];
      if (matches.length === 0) {
        matches = [...content.matchAll(globalPlainUrlRegex)];
        if (matches.length === 0) return [];
        return matches.map(match => this.cleanUrl(match[0]));
      }
      return matches.map(match => this.cleanUrl(match[1]));
    },
    isVideoEmbed(text) {
      const match = text.match(iframeRegex);
      return match;
    },
    hasDataUrl(text) {
      const dataUrlMatch = text.match(dataUrlRegex);
      return dataUrlMatch;
    },
    isAnchorTag(text) {
      const urlMatch = text.match(urlAnchorRegex);
      return urlMatch;
    },
    /**
     * Converts video/embed iframes to plain links (WAF-safe). Use before sending HTML in API payloads.
     */
    sanitizeContentForPayload(html) {
      if (!html || typeof html !== 'string') return html;
      let out = html;

      if (this.isVideoEmbed(out)) {
        let dataUrl = null;
        const dataUrlMatch = this.hasDataUrl(out);
        if (dataUrlMatch) {
          dataUrl = dataUrlMatch[1];
        }
        out = out.replace(
          /<div[^>]*>(.*?<iframe[^>]*>.*?<\/iframe>.*?)<\/div>/,
          `<a href="${dataUrl}" data-url="${dataUrl}" target="_blank" rel="noopener noreferrer"></a>`,
        );
      }

      const hrefContent = out.replace(/<p>(\s*<a\s+href="([^"]+)"[^>]*>(.*?)<\/a>\s*)<\/p>/gi, (match, fullLink, href, text) => {
        const decodeHtml = h => {
          const txt = document.createElement('textarea');
          txt.innerHTML = h;
          return txt.value;
        };
        const decodedHref = decodeHtml(href).replace(/\/$/, '');
        const decodedText = decodeHtml(text).trim().replace(/\/$/, '');
        if (decodedHref === decodedText) {
          return `<a href="${href}" data-url="${href}" target="_blank" rel="noopener noreferrer"></a>`;
        }
        return match;
      });

      if (this.isAnchorTag(out) && hrefContent) {
        out = hrefContent;
      }

      return out;
    },
    extractDataUrl(content) {
      const dataUrlRegex = /<a[^>]*data-url=["']([^"']+)["'][^>]*>/;
      const match = content.match(dataUrlRegex);
      let dataUrl = '';
      if (match) {
        dataUrl = match[1];
      }
      return dataUrl;
    },
    buildVideoUrlFromObject(video) {
      if (!video || typeof video !== 'object') return null;
      const { source, id } = video;
      if (!source || !id) return null;
      if (source === 'vimeo') return `https://vimeo.com/${id}`;
      if (source === 'youtube') return `https://www.youtube.com/watch?v=${id}`;
      if (source === 'facebook') return id;
      return null;
    },
    isDirectVideoUrl(value) {
      if (!value || typeof value !== 'string') return false;
      const socialPatterns = [/vimeo\.com/, /youtube\.com/, /youtu\.be/, /facebook\.com/];
      return !socialPatterns.some(p => p.test(value));
    },
    removeContentUrl(content) {
      return content.replace(/<a[^>]*>(.*?)<\/a>/gi, '');
    },
    handleImageError(event) {
      event.target.src = 'https://assets.staging.crowdblue.com/v2/demo/all/img/imageNotFound.png';
    },
    validatePhone(value) {
      if (!value) {
        return ErrorMessages.REQUIRED;
      } else if (!phoneNumber.test(value)) {
        return ErrorMessages.VALID_PHONE_NUMBER;
      } else if (value.trim().length === 0) {
        return ErrorMessages.NOT_BLANK_MESSAGE;
      } else {
        return '';
      }
    },
    formatPhone(value) {
      if (!value) {
        return value;
      }
      if (/^\d{10}$/.test(value)) {
        return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
      }

      return value;
    },
    validateZip(value) {
      if (!value) {
        return ErrorMessages.REQUIRED;
      } else if (!zipCode.test(value)) {
        return ErrorMessages.VALID_POSTAL_CODE_NUMBER;
      } else if (value.trim().length === 0) {
        return ErrorMessages.NOT_BLANK_MESSAGE;
      } else {
        return '';
      }
    },
    validateInput(value) {
      if (!value) {
        return ErrorMessages.REQUIRED;
      } else if (value.trim().length === 0) {
        return ErrorMessages.NOT_BLANK_MESSAGE;
      } else {
        return '';
      }
    },
    lobbySubject(type, politicianName) {
      if (type == LobbyTypes.ENDORSE) {
        return 'Constituent message endorsing ' + politicianName;
      } else if (type == LobbyTypes.OPPOSE) {
        return 'Constituent message in opposition to ' + politicianName;
      }
    },
    lobbyBodyBase(text) {
      return text
        .split('\n')
        .map(line => line.trimStart())
        .join('\n')
        .trim();
    },
    lobbyBodyParts(user, type, politicianName, news, commentToRep) {
      if (!user) return null;

      if (type == LobbyTypes.ENDORSE) {
        return {
          greeting: this.lobbyBodyBase(`Dear ${politicianName},`),
          body: this.lobbyBodyBase(`As your constituent, I want to thank you for the direction of your policies and leadership.

                I believe the positions you've taken reflect the values and priorities of our community. Please continue to stand up for what matters to regular people and invest in a better future.`),
          closing: this.lobbyBodyBase(`Sincerely,\n${user.name}`),
        };
      } else if (type == LobbyTypes.OPPOSE) {
        return {
          greeting: this.lobbyBodyBase(`Dear ${politicianName},`),
          body: this.lobbyBodyBase(`As your constituent, I want to express my concern about the direction of your policies and priorities.

                I don't feel that your positions reflect the needs or values of people like me in our community. I urge you to reconsider your approach and make decisions that better support regular people and invest in our future.`),
          closing: this.lobbyBodyBase(`Regards,\n${user.name}`),
        };
      } else if (type == LobbyTypes.NONE && news) {
        return {
          greeting: this.lobbyBodyBase(`Hello ${politicianName},

            I'm writing you in response to ${news?.title ? `"${news.title}"` : ''} ${news?.permalink ?? ''}.`),
          body: this.lobbyBodyBase(`WRITE YOUR MESSAGE HERE`),
          closing: this.lobbyBodyBase(`Sincerely,\n${user.name}`),
        };
      } else if (type == LobbyTypes.NONE && commentToRep) {
        return {
          greeting: this.lobbyBodyBase(`Hello ${politicianName},`),
          body: this.lobbyBodyBase(commentToRep),
          closing: this.lobbyBodyBase(`Sincerely,\n${user.name}`),
        };
      }
      return {
        greeting: this.lobbyBodyBase(`Dear ${politicianName},`),
        body: this.lobbyBodyBase(`WRITE YOUR MESSAGE HERE`),
        closing: this.lobbyBodyBase(`Sincerely,\n${user.name}`),
      };
    },
    formatOfficeName(officeName, affiliation) {
      const match = officeName.match(/^(U\.S\. (?:House|Senate)) (.+)$/);
      if (match) {
        return `${match[1]} | ${affiliation} | <span class="primary-color">${match[2]}</span>`;
      }
      return officeName;
    },
    stripHtml(html) {
      if (!html) return '';

      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;

      let text = tempDiv.textContent || tempDiv.innerText || '';
      text = text.replace(/\s+/g, ' ').trim();

      return text;
    },
    stripHtmlTags(html) {
      if (!html) return '';
      const tmp = document.createElement('DIV');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    },
    tagRedirection(id) {
      return `/feed/tags/${id}`;
    },
    getUrlHash() {
      const url = new URL(window.location.href);
      return url.hash;
    },
    lawmakerContext(rep) {
      const parts = [rep.full_name];
      
      if (rep.office_name) {
        parts.push(`Office name: ${rep.office_name}`);
      }
      
      if (rep.office_level) {
        parts.push(`Office level: ${rep.office_level}`);
      }
      
      if (rep.office_branch) {
        parts.push(`Office branch: ${rep.office_branch}`);
      }
      
      return parts.join(' - ');
    },
    hashedUserId(userId) {
      if (window?.GA4Core?.simpleHash) {
        const salt = 'crowdBlue_ga4_';
        return window.GA4Core.simpleHash(salt + userId);
      }
      return null;
    },
    chatUsername(user) {
      const firstname = user?.first_name;
      const lastname = user?.last_name;
      const username = user?.username;
      return lastname?.length > 1 ? `${firstname} ${this.getFirstLetter(lastname)}.` : username;
    },
    getParticipantNames(chat) {
      const names = chat.participants_info?.slice(0, 3).map(p => p.first_name || p.username) || [];
      const remaining = (chat.participants_count || 0) - 3;
      return names.join(', ') + (remaining > 0 ? ` +${remaining}` : '');
    },
    getChatDisplayName(chat) {
      if (chat.is_group) {
        return chat.group_name || this.getParticipantNames(chat);
      }
      return this.chatUsername(chat.user);
    },
    convertImage(img) {
      const imageUrl =
        img &&
        img.settings &&
        img.settings.height &&
        img.settings.width &&
        img.settings.x != null &&
        img.settings.y != null
          ? `https://res.cloudinary.com/crowdpac/image/upload/c_crop,h_${Math.floor(
              img.settings.height,
            )},w_${Math.floor(img.settings.width)},x_${Math.floor(img.settings.x)},y_${Math.floor(
              img.settings.y,
            )}/c_fill,f_auto,h_370,q_auto,w_620/v1/${img.id}`
          : `https://res.cloudinary.com/crowdpac/image/upload/v1/${img.id}`;
    
      return imageUrl;
    },
  },
});
